// Where project payloads live: IndexedDB, behind a synchronous in-memory cache (08.10).
//
// **The problem.** Every project's scenario, timeline, narration timings, review and publish
// package used to sit in localStorage, which Chrome caps at ~5M UTF-16 code units (about 10 MB)
// for the whole origin. Fifteen projects, several of them megabytes, reached the cap and a compile
// could no longer be saved. IndexedDB's quota is a share of the disk, not a fixed 10 MB.
//
// **The constraint.** The app reads payloads synchronously, from dozens of places, often during
// render (`loadScenario()` in a component body, `readRawPayload` in a sweep). IndexedDB is async
// only. Rewriting every reader to await would touch half the app; instead:
//
//  - **Hydration, once, before anything renders.** `bootstrap.ts` awaits `hydrate()` before it even
//    imports the app (`main.tsx`), so no module, no screen and no module-level side effect can run
//    against an empty cache. Hydration reads every record into a `Map` and migrates whatever is
//    still in localStorage (`payloadMigration.ts` decides what; this file does it).
//  - **Reads are synchronous, from the cache.** `getItem` is a `Map` lookup.
//  - **Writes update the cache synchronously, then IndexedDB in the background.** Several writes in
//    one tick become one transaction. `flush()` waits for every write so far to be on disk and
//    says whether one failed — the compile and the import await it before they navigate away.
//  - **A write the page could not finish is journaled.** At `pagehide` anything still in flight is
//    copied into localStorage (there is room now: the payloads have left it) and listed in the
//    journal with its timestamp; the next load moves it into IndexedDB. A page being torn down is
//    not trusted to complete an IndexedDB transaction.
//  - **A failed IndexedDB write falls back to localStorage**, journaled the same way. Only when
//    both fail is the write reported (`onEvent('save-failed')`), and the cache is put back to
//    what is actually stored, so the screen never shows a state that a reload would not.
//  - **Other tabs hear every write** through a `BroadcastChannel` (IndexedDB fires no `storage`
//    event). Two tabs on two projects is a normal way to work, and the registry's rename, the media
//    sweep and the backup all read *other* projects' payloads; a stale copy would let one tab write
//    or sweep from yesterday's picture of the other's project. The receiving tab updates its cache
//    and dispatches a synthetic `storage` event with the key, so existing listeners keep working.
//    The media sweep additionally re-reads the disk first (`refreshFromDisk`) — the one place a
//    stale picture would delete something.
//
// **Where IndexedDB is missing** (Node tests, the render bundle) the store is a pass-through to
// localStorage — exactly the behaviour before this file existed.
//
// Only the five payload slots (`isPayloadKey`) live here. Everything else under
// `storydress:project:<id>:` (render job, recent renders, backup state, the project brief, the
// council's report) is small and stays in localStorage — README "Persistence".

import {
  isPayloadKey,
  isPayloadStub,
  JOURNAL_KEY,
  PAYLOAD_STUB,
  planPayloadMigration,
  type Journal,
  type MigrationStep,
  type StoredPayload,
} from './payloadMigration';

/** The slice of the Web Storage API this file uses — `localStorage` in the app, a fake in tests. */
export interface KeyValueStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The IndexedDB side, as the store needs it. `payloadIdb.ts` is the real one. */
export interface PayloadBackend {
  readAll(): Promise<Map<string, StoredPayload>>;
  read(key: string): Promise<StoredPayload | null>;
  /** One transaction; resolves once committed, rejects with the transaction's error. */
  write(puts: [string, StoredPayload][], deletes: string[]): Promise<void>;
  /** Keep `record` under `superseded`, pruning that key's older archived copies. */
  archive(key: string, record: StoredPayload, reason: string): Promise<void>;
}

export type PayloadMessage =
  | { t: 'set'; k: string; v: string; at: number }
  | { t: 'del'; k: string; at: number }
  /** "My write of this key failed — what you heard from me is not on disk; read it again." */
  | { t: 'stale'; k: string };

export interface Messenger {
  post(message: PayloadMessage): void;
  listen(handler: (message: PayloadMessage) => void): void;
}

export type StoreEvent =
  | { type: 'save-failed'; key: string; error: unknown }
  | { type: 'remote-change'; key: string };

export interface StoreDeps {
  /** Resolved on every call, never captured: tests install their fake after import. */
  local: () => KeyValueStorage | null;
  backend?: PayloadBackend | null;
  messenger?: Messenger | null;
  now?: () => number;
  /** Runs the migration under a cross-tab lock where the browser has one. */
  withLock?: <T>(fn: () => Promise<T>) => Promise<T>;
  onEvent?: (event: StoreEvent) => void;
}

export interface HydrationReport {
  /** `idb`: payloads are served from IndexedDB. `local`: pass-through to localStorage. */
  mode: 'idb' | 'local';
  /** Hydration could not read IndexedDB although a previous load had moved payloads into it. */
  unavailable?: boolean;
  records: number;
  moved: string[];
  /** Keys left in localStorage, and why — they are read from there until a later load moves them. */
  kept: { key: string; reason: string }[];
  ms: number;
}

export interface FlushResult {
  ok: boolean;
  /** Keys whose newest value is on disk nowhere — neither IndexedDB nor the localStorage fallback. */
  unsaved: string[];
  error?: unknown;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (err && typeof err === 'object' && 'name' in err) return String((err as { name: unknown }).name);
  return String(err);
}

export function createPayloadStore(deps: StoreDeps) {
  const now = deps.now ?? (() => Date.now());
  const local = () => {
    try {
      return deps.local();
    } catch {
      return null;
    }
  };
  let mode: 'idb' | 'local' = 'local';
  /** What this tab believes each key holds — the source of every read in `idb` mode. */
  const cache = new Map<string, StoredPayload>();
  /** What this tab knows to be on disk (IndexedDB, or the localStorage fallback). */
  const durable = new Map<string, StoredPayload>();
  /** Waiting for the next transaction. `null` = delete. */
  let pending = new Map<string, StoredPayload | null>();
  /** In the transaction running now. */
  let inflight = new Map<string, StoredPayload | null>();
  const unsaved = new Map<string, unknown>();
  let lastError: unknown;
  let lastAt = 0;
  let flushing: Promise<void> | null = null;
  let buffered: PayloadMessage[] | null = null;

  const stamp = () => {
    lastAt = Math.max(now(), lastAt + 1);
    return lastAt;
  };

  const emit = (event: StoreEvent) => {
    try {
      deps.onEvent?.(event);
    } catch (err) {
      console.warn('[payloads] event listener threw', err);
    }
  };

  const post = (message: PayloadMessage) => {
    try {
      deps.messenger?.post(message);
    } catch (err) {
      console.warn('[payloads] could not tell the other tabs', err);
    }
  };

  // ── Journal (localStorage) ────────────────────────────────────────────────
  const readJournal = (): Journal => {
    try {
      const raw = local()?.getItem(JOURNAL_KEY);
      const parsed = raw ? (JSON.parse(raw) as Journal) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };
  const writeJournal = (journal: Journal) => {
    const ls = local();
    if (!ls) return;
    if (Object.keys(journal).length === 0) ls.removeItem(JOURNAL_KEY);
    else ls.setItem(JOURNAL_KEY, JSON.stringify(journal));
  };
  /** Copies one write into localStorage and journals it. Throws when localStorage refuses. */
  const journalWrite = (key: string, entry: StoredPayload | null, at: number) => {
    const ls = local();
    if (!ls) throw new Error('localStorage unavailable');
    if (entry) ls.setItem(key, entry.v);
    else ls.removeItem(key);
    const journal = readJournal();
    journal[key] = entry ? { at } : { at, deleted: true };
    writeJournal(journal);
  };
  const forgetJournal = (keys: Iterable<string>) => {
    try {
      const journal = readJournal();
      let changed = false;
      for (const key of keys) {
        if (key in journal) {
          delete journal[key];
          changed = true;
        }
      }
      if (changed) writeJournal(journal);
    } catch {
      /* a journal line left behind is harmless: the next load compares timestamps */
    }
  };

  /** localStorage holds the stub for a key that is in IndexedDB — see `PAYLOAD_STUB`. */
  const stubLocal = (key: string) => {
    const ls = local();
    if (!ls) return;
    try {
      if (ls.getItem(key) !== PAYLOAD_STUB) ls.setItem(key, PAYLOAD_STUB);
    } catch {
      // Replacing a big value with a 40-byte one should never fail; if it does, the copy is
      // removed instead — IndexedDB already holds the same string, read back and compared.
      try {
        ls.removeItem(key);
      } catch {
        /* nothing else to try */
      }
    }
  };

  // ── Writing ───────────────────────────────────────────────────────────────
  const committed = (key: string, entry: StoredPayload | null) => {
    if (entry) durable.set(key, entry);
    else durable.delete(key);
    unsaved.delete(key);
    if (entry) stubLocal(key);
    else {
      try {
        local()?.removeItem(key);
      } catch {
        /* the stub of a deleted key is harmless */
      }
    }
  };

  const failed = (key: string, entry: StoredPayload | null, err: unknown) => {
    lastError = err;
    try {
      journalWrite(key, entry, entry?.at ?? stamp());
      unsaved.delete(key);
      console.warn(`[payloads] IndexedDB write failed for ${key}; kept in localStorage instead (${errorMessage(err)})`);
      return;
    } catch (fallbackErr) {
      console.error(`[payloads] SAVE FAILED: ${key} — IndexedDB: ${errorMessage(err)}; localStorage: ${errorMessage(fallbackErr)}`);
    }
    unsaved.set(key, err);
    // The screen must not show what a reload would not: put the cache back to what is stored,
    // unless something newer was written meanwhile (that write gets its own chance).
    const current = cache.get(key) ?? null;
    if (current === entry && !pending.has(key)) {
      const onDisk = durable.get(key);
      if (onDisk) cache.set(key, onDisk);
      else cache.delete(key);
      post({ t: 'stale', k: key });
    }
    emit({ type: 'save-failed', key, error: err });
  };

  const drain = async () => {
    const backend = deps.backend;
    if (!backend) return;
    while (pending.size > 0) {
      const batch = pending;
      pending = new Map();
      inflight = batch;
      const puts: [string, StoredPayload][] = [];
      const deletes: string[] = [];
      for (const [key, entry] of batch) {
        if (entry) puts.push([key, entry]);
        else deletes.push(key);
      }
      try {
        await backend.write(puts, deletes);
        for (const [key, entry] of batch) committed(key, entry);
        forgetJournal(batch.keys());
      } catch (err) {
        for (const [key, entry] of batch) failed(key, entry, err);
      } finally {
        inflight = new Map();
      }
    }
  };

  const scheduleFlush = () => {
    if (flushing) return;
    flushing = Promise.resolve()
      .then(drain)
      .finally(() => {
        flushing = null;
        if (pending.size > 0) scheduleFlush();
      });
  };

  const isIdbKey = (key: string) => mode === 'idb' && isPayloadKey(key);

  function getItem(key: string): string | null {
    if (isIdbKey(key)) return cache.get(key)?.v ?? null;
    return local()?.getItem(key) ?? null;
  }

  /** Throws only in pass-through mode, exactly where `localStorage.setItem` would. */
  function setItem(key: string, value: string): void {
    if (!isIdbKey(key)) {
      const ls = local();
      if (!ls) throw new Error('localStorage unavailable');
      ls.setItem(key, value);
      return;
    }
    const entry = { v: value, at: stamp() };
    cache.set(key, entry);
    pending.set(key, entry);
    post({ t: 'set', k: key, v: value, at: entry.at });
    scheduleFlush();
  }

  function removeItem(key: string): void {
    if (!isIdbKey(key)) {
      local()?.removeItem(key);
      return;
    }
    const at = stamp();
    cache.delete(key);
    pending.set(key, null);
    // A leftover copy or the stub goes now: a delete is not something to keep a fallback of.
    try {
      local()?.removeItem(key);
    } catch {
      /* ignore */
    }
    post({ t: 'del', k: key, at });
    scheduleFlush();
  }

  /** Every payload key this tab knows to exist (pass-through: what localStorage lists). */
  function keys(): string[] {
    if (mode === 'idb') return [...cache.keys()];
    const out: string[] = [];
    const ls = local();
    if (!ls) return out;
    try {
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (key && isPayloadKey(key)) out.push(key);
      }
    } catch {
      /* unreadable storage lists nothing */
    }
    return out;
  }

  async function flush(): Promise<FlushResult> {
    while (flushing || pending.size > 0) {
      if (!flushing) scheduleFlush();
      await flushing;
    }
    return { ok: unsaved.size === 0, unsaved: [...unsaved.keys()], error: unsaved.size ? lastError : undefined };
  }

  /**
   * Replaces several keys in ONE transaction — the import's "every payload together". Nothing in
   * the cache changes until the transaction commits; on failure the error is thrown and the cache,
   * the disk and the other tabs are exactly as they were.
   */
  async function writeAtomically(entries: Record<string, string | null>): Promise<void> {
    const list = Object.entries(entries);
    if (mode !== 'idb' || !deps.backend) {
      const ls = local();
      if (!ls) throw new Error('localStorage unavailable');
      const previous = list.map(([key]) => [key, ls.getItem(key)] as const);
      try {
        for (const [key, value] of list) {
          if (value === null) ls.removeItem(key);
          else ls.setItem(key, value);
        }
      } catch (err) {
        for (const [key, value] of previous) {
          try {
            if (value === null) ls.removeItem(key);
            else ls.setItem(key, value);
          } catch {
            /* the throw below is what the caller reports */
          }
        }
        throw err;
      }
      return;
    }
    await flush();
    const at = stamp();
    const puts: [string, StoredPayload][] = [];
    const deletes: string[] = [];
    for (const [key, value] of list) {
      if (value === null) deletes.push(key);
      else puts.push([key, { v: value, at }]);
    }
    await deps.backend.write(puts, deletes);
    for (const [key, entry] of puts) {
      cache.set(key, entry);
      committed(key, entry);
      post({ t: 'set', k: key, v: entry.v, at });
    }
    for (const key of deletes) {
      cache.delete(key);
      committed(key, null);
      post({ t: 'del', k: key, at });
    }
  }

  /**
   * Re-reads IndexedDB into the cache — for the one decision that must not be made from a stale
   * picture of another tab's project: which media nobody references any more.
   */
  async function refreshFromDisk(): Promise<void> {
    if (mode !== 'idb' || !deps.backend) return;
    await flush();
    const startedAt = now();
    const disk = await deps.backend.readAll();
    for (const [key, record] of disk) {
      if (pending.has(key) || inflight.has(key)) continue;
      const mine = cache.get(key);
      if (!mine || record.at >= mine.at) {
        cache.set(key, record);
        durable.set(key, record);
      }
    }
    for (const [key, mine] of [...cache]) {
      if (disk.has(key) || pending.has(key) || inflight.has(key)) continue;
      // Not on disk: deleted by another tab — unless it is newer than this read (another tab's
      // write still on its way) or this tab's copy lives in the localStorage fallback.
      if (mine.at >= startedAt) continue;
      if (local()?.getItem(key) === mine.v) continue;
      cache.delete(key);
      durable.delete(key);
    }
  }

  /** At `pagehide`: whatever is not known to be on disk goes into localStorage, journaled. */
  function journalPending(): void {
    if (mode !== 'idb') return;
    const outstanding = new Map<string, StoredPayload | null>(inflight);
    for (const [key, entry] of pending) outstanding.set(key, entry);
    for (const [key, entry] of outstanding) {
      try {
        journalWrite(key, entry, entry?.at ?? stamp());
      } catch (err) {
        console.error(`[payloads] could not journal ${key} at unload`, err);
      }
    }
  }

  function applyRemote(message: PayloadMessage): void {
    if (buffered) {
      buffered.push(message);
      return;
    }
    if (mode !== 'idb' || !isPayloadKey(message.k)) return;
    const key = message.k;
    if (pending.has(key) || inflight.has(key)) return; // this tab's own newer write stands
    if (message.t === 'stale') {
      void deps.backend
        ?.read(key)
        .then((record) => {
          if (pending.has(key) || inflight.has(key)) return;
          if (record) cache.set(key, record);
          else cache.delete(key);
          emit({ type: 'remote-change', key });
        })
        .catch(() => undefined);
      return;
    }
    const mine = cache.get(key);
    if (mine && mine.at > message.at) return;
    if (message.t === 'set') cache.set(key, { v: message.v, at: message.at });
    else cache.delete(key);
    lastAt = Math.max(lastAt, message.at);
    emit({ type: 'remote-change', key });
  }

  // ── Hydration and migration ───────────────────────────────────────────────

  /** Payload keys in localStorage that hold real data (not the stub), and whether any stub exists. */
  const scanLocal = () => {
    const entries: { key: string; value: string }[] = [];
    let stubs = 0;
    const ls = local();
    if (!ls) return { entries, stubs };
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (!key || !isPayloadKey(key)) continue;
      const value = ls.getItem(key);
      if (value === null) continue;
      if (isPayloadStub(value)) stubs++;
      else entries.push({ key, value });
    }
    return { entries, stubs };
  };

  async function runStep(
    backend: PayloadBackend,
    step: MigrationStep,
    stored: Map<string, StoredPayload>,
    report: HydrationReport,
    leftovers: Map<string, string>,
    done: Set<string>,
  ): Promise<void> {
    const key = step.key;
    switch (step.kind) {
      case 'already-stored':
      case 'stored-is-newer':
        stubLocal(key);
        done.add(key);
        return;
      case 'forget-journal':
        done.add(key);
        return;
      case 'delete':
        await backend.write([], [key]);
        stored.delete(key);
        done.add(key);
        return;
      case 'move': {
        const previous = stored.get(key);
        const record: StoredPayload = { v: step.value, at: step.at };
        try {
          if (step.archive) await backend.archive(key, step.archive, 'replaced by a localStorage copy');
          await backend.write([[key, record]], []);
        } catch (err) {
          leftovers.set(key, step.value);
          report.kept.push({ key, reason: `IndexedDB write failed: ${errorMessage(err)}` });
          console.error(`[payloads] could not move ${key}; it stays in localStorage`, err);
          return;
        }
        const back = await backend.read(key).catch(() => null);
        if (!back || back.v !== step.value) {
          // Never stub on a mismatch. Put the disk back the way it was; the localStorage copy is
          // still the one the app reads.
          try {
            if (previous) await backend.write([[key, previous]], []);
            else await backend.write([], [key]);
          } catch {
            /* the localStorage copy wins on read regardless */
          }
          leftovers.set(key, step.value);
          report.kept.push({ key, reason: 'read-back did not match' });
          console.error(`[payloads] ${key} read back different from what was written; it stays in localStorage`);
          return;
        }
        stored.set(key, record);
        stubLocal(key);
        report.moved.push(key);
        done.add(key);
        return;
      }
    }
  }

  async function hydrate(): Promise<HydrationReport> {
    const started = now();
    const report: HydrationReport = { mode: 'local', records: 0, moved: [], kept: [], ms: 0 };
    const backend = deps.backend;
    if (!backend) {
      report.ms = now() - started;
      return report;
    }
    buffered = [];
    deps.messenger?.listen((message) => applyRemote(message));
    try {
      const migrate = async () => {
        const stored = await backend.readAll();
        const { entries } = scanLocal();
        const journal = readJournal();
        const steps = planPayloadMigration({ local: entries, journal, stored, now: started });
        const leftovers = new Map<string, string>();
        const done = new Set<string>();
        for (const step of steps) {
          try {
            await runStep(backend, step, stored, report, leftovers, done);
          } catch (err) {
            if (step.kind === 'move') leftovers.set(step.key, step.value);
            report.kept.push({ key: step.key, reason: errorMessage(err) });
            console.error(`[payloads] migration step ${step.kind} ${step.key} failed`, err);
          }
        }
        forgetJournal(done);
        return { stored, leftovers };
      };
      const { stored, leftovers } = deps.withLock ? await deps.withLock(migrate) : await migrate();
      cache.clear();
      durable.clear();
      for (const [key, record] of stored) {
        cache.set(key, record);
        durable.set(key, record);
        lastAt = Math.max(lastAt, record.at);
      }
      // A key that could not be moved is read from localStorage, as before — it is the newest copy.
      for (const [key, value] of leftovers) cache.set(key, { v: value, at: stamp() });
      mode = 'idb';
      report.mode = 'idb';
      report.records = cache.size;
    } catch (err) {
      // IndexedDB would not open or read. If no payload was ever moved, localStorage still holds
      // everything and the pass-through is exactly the old app. If one was, the stubs say so, and
      // the caller must not render projects as empty — `bootstrap.ts` shows a message instead.
      console.error('[payloads] could not read IndexedDB', err);
      mode = 'local';
      report.mode = 'local';
      if (scanLocal().stubs > 0) report.unavailable = true;
    }
    const queued = buffered ?? [];
    buffered = null;
    for (const message of queued) applyRemote(message);
    report.ms = now() - started;
    return report;
  }

  /** Approximate size per payload key, largest first — for the storage line in error messages. */
  function sizes(): { key: string; chars: number }[] {
    const out: { key: string; chars: number }[] = [];
    for (const key of keys()) out.push({ key, chars: getItem(key)?.length ?? 0 });
    return out.sort((a, b) => b.chars - a.chars);
  }

  return {
    mode: () => mode,
    hydrate,
    getItem,
    setItem,
    removeItem,
    keys,
    flush,
    writeAtomically,
    refreshFromDisk,
    journalPending,
    applyRemote,
    sizes,
  };
}

export type PayloadStore = ReturnType<typeof createPayloadStore>;
